import React, { useState } from 'react';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
  Collapse,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  ListAlt as ListAltIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Category as CategoryIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Backup as BackupIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Home as HomeIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  Close as CloseIcon,
  Message as MessageIcon,
  AssignmentInd as AssignmentIndIcon,
  Insights as InsightsIcon,
  Summarize as SummarizeIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const ModernSidebar = ({ drawerWidth = 260, isMobile, open, onClose }) => {
  const [expandedSections, setExpandedSections] = useState({
    complaints: true,
    admin: true
  });

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const role = localStorage.getItem('role');
  


  const handleSectionToggle = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleNavigationClick = (path) => {
    if (isMobile) {
      onClose();
    }
    navigate(path);
  };

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  const menuItemStyles = {
    borderRadius: 2,
    mx: 1,
    mb: 0.5,
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: (theme.palette.mode === 'dark' || !theme.palette.mode)
        ? alpha(theme.palette.primary.main, 0.15)
        : alpha(theme.palette.primary.main, 0.08),
      transform: 'translateX(4px)',
      '& .MuiListItemIcon-root': {
        color: theme.palette.primary.main,
        transform: 'scale(1.1)'
      }
    },
    '&.active': {
      backgroundColor: (theme.palette.mode === 'dark' || !theme.palette.mode)
        ? alpha(theme.palette.primary.main, 0.2)
        : alpha(theme.palette.primary.main, 0.12),
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      '& .MuiListItemIcon-root': {
        color: theme.palette.primary.main
      },
      '& .MuiListItemText-primary': {
        color: theme.palette.primary.main,
        fontWeight: 600
      }
    }
  };

  const mainNavigationItems = [
    { text: 'Overview', icon: <HomeIcon />, path: '/' },
  ];

  const complaintItems = [
    { text: 'New Complaint', icon: <AddCircleOutlineIcon />, path: '/complaint_new' },
    { text: 'All Complaints', icon: <ListAltIcon />, path: '/complaints' },
    { text: 'My Assigned', icon: <AssignmentIndIcon />, path: '/my-assigned' },
    { text: 'Pending', icon: <HourglassEmptyIcon />, path: '/pending-complaints' },
    { text: 'Resolved', icon: <CheckCircleOutlineIcon />, path: '/resolved-complaints' }
  ];

  const managementItems = [
    { text: 'Analytics', icon: <InsightsIcon />, path: '/analytics' },
    { text: 'Categories', icon: <CategoryIcon />, path: '/category' },
    { text: 'Assemblies', icon: <AssessmentIcon />, path: '/assemblies' },
    { text: 'Districts', icon: <AssessmentIcon />, path: '/districts' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' }
  ];

  const adminItems = [
    { text: 'User Management', icon: <PeopleIcon />, path: '/users' },
    { text: 'Reports', icon: <SummarizeIcon />, path: '/reports' },
    { text: 'Audit Log', icon: <HistoryIcon />, path: '/audit-logs' },
    { text: 'SMS Management', icon: <MessageIcon />, path: '/sms-management' },
    { text: 'Backup & Restore', icon: <BackupIcon />, path: '/backup-restore' },
    { text: 'Security', icon: <SecurityIcon />, path: '/security' }
  ];

  const renderNavItem = (item) => (
    <ListItem
      key={item.text}
      onClick={() => handleNavigationClick(item.path)}
      sx={{
        ...menuItemStyles,
        ...(isActiveRoute(item.path) && { className: 'active' }),
        cursor: 'pointer',
        py: 1,
        px: 2,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}
    >
      <ListItemIcon sx={{ 
        minWidth: 40,
        transition: 'all 0.2s ease-in-out',
        color: isActiveRoute(item.path) ? theme.palette.primary.main : 'text.secondary'
      }}>
        {item.icon}
      </ListItemIcon>
      <ListItemText 
        primary={item.text}
        primaryTypographyProps={{
          fontWeight: isActiveRoute(item.path) ? 600 : 500,
          fontSize: '0.9rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
        sx={{
          minWidth: 0,
          flex: 1
        }}
      />
    </ListItem>
  );

  const renderSection = (title, items, sectionKey) => (
    <Box key={sectionKey}>
      <ListItem
        onClick={() => handleSectionToggle(sectionKey)}
        sx={{
          cursor: 'pointer',
          py: 1,
          px: 2,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          '&:hover': {
            backgroundColor: (theme.palette.mode === 'dark' || !theme.palette.mode)
              ? alpha(theme.palette.primary.main, 0.1)
              : alpha(theme.palette.primary.main, 0.05)
          }
        }}
      >
        <ListItemText 
          primary={
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600, 
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {title}
            </Typography>
          }
        />
        {expandedSections[sectionKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItem>
      
      <Collapse in={expandedSections[sectionKey]} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {items.map(renderNavItem)}
        </List>
      </Collapse>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          mt: isMobile ? 0 : 2,
          background: (theme.palette.mode === 'dark' || !theme.palette.mode) 
            ? 'linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 100%)'
            : 'linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)',
          borderRight: '1px solid',
          borderColor: (theme.palette.mode === 'dark' || !theme.palette.mode) ? 'rgba(255,255,255,0.1)' : 'divider',
          boxShadow: isMobile 
            ? (theme.palette.mode === 'dark' || !theme.palette.mode)
              ? '4px 0 20px rgba(0,0,0,0.6)' 
              : '4px 0 20px rgba(0,0,0,0.3)'
            : (theme.palette.mode === 'dark' || !theme.palette.mode)
              ? '2px 0 8px rgba(0,0,0,0.3)'
              : '2px 0 8px rgba(0,0,0,0.1)',
          height: isMobile ? '100%' : 'calc(100% - 64px)',
          top: isMobile ? 0 : 64,
          position: isMobile ? 'fixed' : 'relative',
          zIndex: 1200,
          // Mobile-specific improvements
                      ...(isMobile && {
              '& .MuiBackdrop-root': {
                backgroundColor: (theme.palette.mode === 'dark' || !theme.palette.mode)
                  ? 'rgba(0, 0, 0, 0.7)' 
                  : 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(4px)'
              }
            })
        }
      }}
      ModalProps={{
        keepMounted: true,
        // Improve mobile touch handling
        disableScrollLock: false,
        disableEnforceFocus: false,
        disableAutoFocus: false
      }}
    >
      <Box sx={{ 
        overflow: 'auto', 
        py: 0,
        width: '100%',
        maxWidth: '100%',
        '& .MuiListItemText-root': {
          minWidth: 0,
          flex: 1
        },
        '& .MuiListItemText-primary': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }
      }}>
        {/* Mobile Swipe Indicator */}
        {isMobile && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            py: 0.5,
            mb: 0.5
          }}>
            <Box sx={{
              width: 40,
              height: 4,
              backgroundColor: (theme.palette.mode === 'dark' || !theme.palette.mode) ? 'rgba(255,255,255,0.3)' : 'divider',
              borderRadius: 2,
              opacity: 0.6
            }} />
          </Box>
        )}
        
        {/* Header - only show close button for mobile */}
        {isMobile && (
          <Box sx={{ px: 3, py: 1, mb: 1, position: 'relative' }}>
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main'
                }
              }}
              aria-label="Close sidebar"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        )}

        <List component="nav" sx={{ 
          px: 0,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Main Navigation */}
          <Box sx={{ mb: 1 }}>
            <Typography 
              variant="overline" 
              sx={{ 
                px: 3, 
                py: 0.5, 
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.75rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              Main
            </Typography>
            {mainNavigationItems.map(renderNavItem)}
          </Box>

          {/* Complaints Section */}
          {renderSection('Complaints', complaintItems, 'complaints')}

          {/* Management Section */}
          <Divider sx={{ 
            my: 1, 
            mx: 2,
            borderColor: (theme.palette.mode === 'dark' || !theme.palette.mode) ? 'rgba(255,255,255,0.1)' : 'divider'
          }} />
          {renderSection('Management', managementItems, 'management')}

          {/* Admin Section */}
          {role === 'admin' && (
            <>
              <Divider sx={{ 
                my: 2, 
                mx: 2,
                borderColor: (theme.palette.mode === 'dark' || !theme.palette.mode) ? 'rgba(255,255,255,0.1)' : 'divider'
              }} />
                             <Box sx={{ mb: 2 }}>
                 <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 1 }}>
                  <Typography 
                    variant="overline" 
                    sx={{ 
                      color: 'text.secondary',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Admin Tools
                  </Typography>
                  <Chip 
                    label="Admin" 
                    size="small" 
                    color="warning" 
                    sx={{ ml: 1, height: 20 }}
                  />
                </Box>
                {adminItems.map(renderNavItem)}
              </Box>
            </>
          )}
        </List>

        {/* Footer */}
        <Box sx={{ 
          position: 'absolute', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          p: 2,
          borderTop: '1px solid',
          borderColor: (theme.palette.mode === 'dark' || !theme.palette.mode) ? 'rgba(255,255,255,0.1)' : 'divider',
          backgroundColor: (theme.palette.mode === 'dark' || !theme.palette.mode) ? 'rgba(0,0,0,0.2)' : 'background.paper'
        }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'text.secondary', 
              textAlign: 'center', 
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            KC Venugopal MP • v2.0
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ModernSidebar; 